/** Runtime helpers for unknown API / JSON payloads → trusted JS shapes. */

const DEFAULT_LIST_KEYS = [
  'Records',
  'records',
  'Data',
  'data',
  'Items',
  'items',
  'Tags',
  'tags',
] as const;

export function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** True when value is a non-null object that has a `Records` key (any value). */
export function hasRecords(value: unknown): value is { Records: unknown } {
  return isRecord(value) && 'Records' in value;
}

/** True when value has `Records` as an array. */
export function isRecordsEnvelope(
  value: unknown,
): value is { Records: unknown[] } {
  return isRecord(value) && Array.isArray(value.Records);
}

/**
 * Extract a list from a bare array or `{ Records: unknown[] }`.
 * Returns `[]` for any other shape.
 */
export function extractRecordsList(data: unknown): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (isRecordsEnvelope(data)) {
    return data.Records;
  }
  return [];
}

/**
 * Extract a list from a bare array or the first nested array among common
 * envelope keys (Records/Data/Items/Tags and camelCase variants).
 */
export function extractListFromPayload(
  data: unknown,
  keys: readonly string[] = DEFAULT_LIST_KEYS,
): unknown[] {
  if (Array.isArray(data)) {
    return data;
  }
  if (!isRecord(data)) {
    return [];
  }
  for (const key of keys) {
    const nested = data[key];
    if (Array.isArray(nested)) {
      return nested;
    }
  }
  return [];
}

export function coerceFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const n = Number(value.trim());
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

/**
 * Coerce a non-empty trimmed string. Finite numbers become their string form.
 */
export function coerceNonEmptyString(value: unknown): string | null {
  if (typeof value === 'string') {
    const t = value.trim();
    return t.length > 0 ? t : null;
  }
  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value);
  }
  return null;
}

/** Parse JSON without throwing; returns `null` on failure. */
export function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw) as unknown;
  } catch {
    return null;
  }
}
